import { Address, ContactInfo, Meta } from '@tremaze/shared/models';
import {
  Deserializable,
  staticImplements,
} from '@tremaze/shared/util-decorators';
import { Gender } from '@tremaze/shared/feature/gender/types';
import { FileStorage } from '@tremaze/shared/feature/file-storage/types';
import { TremazeDate } from '@tremaze/shared/util-date';
import { UserType } from '@tremaze/shared/feature/user/types';
import { UserInstitution } from '@tremaze/shared/feature/user/feature/allocation/types';
import { Tenant } from '@tremaze/shared/feature/tenant/types';

export type AuthenticatedUserTenant = Tenant;

@staticImplements<Deserializable<AuthenticatedUser>>()
export class AuthenticatedUser {
  constructor(
    readonly userId: string,
    readonly meta: Meta,
    readonly username: string,
    readonly email?: string,
    readonly enabled?: boolean,
    readonly mainAdmin?: boolean,
    readonly userRoles?: string[],
    readonly userPrivileges?: string[],
    readonly firstName?: string,
    readonly lastName?: string,
    readonly contact?: ContactInfo,
    readonly gender?: Gender,
    readonly userInstitutions: UserInstitution[] = [],
    readonly profileImage?: FileStorage,
    readonly address?: Address,
    readonly birth?: TremazeDate,
    readonly userTypes?: UserType[],
    public tenants?: AuthenticatedUserTenant[]
  ) {}

  get instIds(): string[] {
    return this.userInstitutions?.map((u) => u.institution?.id) ?? [];
  }

  get initials(): string {
    const arr = [];
    if (this.firstName?.length) {
      arr.push(this.firstName[0].toUpperCase());
    }
    if (this.lastName?.length) {
      arr.push(this.lastName[0].toUpperCase());
    }
    return arr.join('');
  }

  static deserialize(data: any): AuthenticatedUser {
    return !data
      ? null
      : new AuthenticatedUser(
          data.userId,
          Meta.deserialize(data.meta),
          data.username,
          data.email,
          data.enabled,
          data.mainAdmin,
          data.userRoles,
          data.userPrivileges,
          data.firstname || data.firstName,
          data.lastname || data.lastName,
          ContactInfo.deserialize(
            data.contact ?? {
              mobile: data.mobile,
              email: data.email,
              phone: data.phone,
            }
          ),
          Gender.deserialize(data.gender),
          data.userInstitutionPrivileges,
          FileStorage.deserialize(data.profileImage ?? data.avatar),
          Address.deserialize(data.address),
          TremazeDate.deserialize(data.birth),
          data.userTypes
        );
  }

}

@staticImplements<Deserializable<TokenInfo>>()
export class TokenInfo {
  constructor(
    readonly access_token: string,
    readonly refresh_token: string,
    readonly expiration: number,
    readonly expires_in: number,
    readonly scope: string,
    readonly token_type: string
  ) {}

  static deserialize(data: any): TokenInfo {
    return !data
      ? null
      : new TokenInfo(
          data.access_token,
          data.refresh_token,
          data.expiration,
          data.expires_in,
          data.scope,
          data.token_type
        );
  }
}
